const CAMPAIGN_QUERY_KEYS = new Set([
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
]);

const URL_FIELDS = new Set([
  "page_location",
  "page_path",
  "page_referrer",
  "cta_destination",
  "outbound_url",
  "file_url",
  "requested_path",
]);

const MAX_STRING_LENGTH = 160;

// Evita che valori personali o segreti finiscano negli eventi anche quando un
// chiamante passa parametri extra non previsti dal contratto.
const BLOCKED_PARAM_KEY = /(?:^|_)(?:email|phone|token|password|secret|message|notes)(?:_|$)/i;
const BLOCKED_EVENT_KEYS = new Set(["company"]);
const RESERVED_PARAM_KEYS = new Set([
  "event",
  "schema_version",
  "page_location",
  "page_path",
  "page_referrer",
  "page_title",
  "page_language",
  "form_id",
  "form_type",
]);

function clamp(value) {
  return typeof value === "string" ? value.trim().slice(0, MAX_STRING_LENGTH) : value;
}

export function sanitizeCampaignQuery(search) {
  const source = typeof search === "string" ? search : "";
  const input = new URLSearchParams(source.startsWith("?") ? source : `?${source}`);
  const output = new URLSearchParams();
  for (const [key, value] of input.entries()) {
    if (!CAMPAIGN_QUERY_KEYS.has(key) || !value) continue;
    output.set(key, clamp(value));
  }
  const query = output.toString();
  return query ? `?${query}` : "";
}

// Restituisce origin + pathname e, per le pagine interne, solo i parametri di
// campagna ammessi. Fragment e query arbitrarie non entrano nel payload.
export function sanitizeUrl(value, { allowCampaignQuery = false } = {}) {
  if (typeof value !== "string" || !value) return undefined;
  try {
    const url = new URL(value, "https://mon5.eu");
    const query = allowCampaignQuery ? sanitizeCampaignQuery(url.search) : "";
    const isRelative = !/^[a-z][a-z\d+.-]*:/i.test(value) && !value.startsWith("//");
    const base = isRelative ? `${url.pathname}` : `${url.origin}${url.pathname}`;
    return `${base || "/"}${query}`;
  } catch {
    return undefined;
  }
}

export function sanitizeEventParams(params = {}, { allowReserved = false } = {}) {
  const safe = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (BLOCKED_EVENT_KEYS.has(key)) continue;
    if (!allowReserved && RESERVED_PARAM_KEYS.has(key)) continue;
    if (BLOCKED_PARAM_KEY.test(key) && key !== "user_data") continue;
    if (URL_FIELDS.has(key)) {
      const sanitized = sanitizeUrl(value, {
        allowCampaignQuery: key === "page_location" || key === "page_path",
      });
      if (sanitized) safe[key] = sanitized;
      continue;
    }
    if (key === "user_data") {
      // user_data viene costruito separatamente e contiene solo hash. Non
      // accettare valori arbitrari passati tramite extra.
      if (value && typeof value === "object" && !Array.isArray(value)) safe[key] = value;
      continue;
    }
    if (key === "first_touch" || key === "last_non_direct_touch") {
      if (!value || typeof value !== "object" || Array.isArray(value)) continue;
      const touch = {};
      for (const touchKey of [
        "gclid",
        "gbraid",
        "wbraid",
        "fbclid",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "landing_page",
        "captured_at",
      ]) {
        if (value[touchKey] === undefined) continue;
        touch[touchKey] =
          touchKey === "landing_page" ? sanitizeUrl(value[touchKey]) : clamp(value[touchKey]);
      }
      safe[key] = touch;
      continue;
    }
    if (typeof value === "string") safe[key] = clamp(value);
    else if (typeof value === "number" || typeof value === "boolean") safe[key] = value;
  }
  return safe;
}
